import React from 'react'
import { Clock, Calendar, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTimezone } from '@/hooks/useTimezone'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { getRelativeTime, getSmartRelativeTime, getContextualTime, type RelativeTimeOptions } from '@/lib/timezone-utils'
import { MultiTimezoneTooltip, CompactMultiTimezoneTooltip } from './MultiTimezoneTooltip'
import { useTeamTimezones } from '@/hooks/useTeamTimezones'

interface TimeWithTimezoneProps {
  /** The date/time to display */
  date: Date | string
  /** Format string for the date/time display */
  format?: string
  /** Whether to show timezone abbreviation inline */
  showTimezone?: boolean
  /** Whether to show relative time (Today, Tomorrow, etc.) */
  showRelative?: boolean
  /** Relative time mode: 'simple' for basic relative, 'smart' for threshold-based, 'contextual' for intelligent formatting */
  relativeMode?: 'simple' | 'smart' | 'contextual'
  /** Context for contextual relative mode */
  relativeContext?: 'compact' | 'normal' | 'detailed'
  /** Options for relative time display */
  relativeOptions?: RelativeTimeOptions
  /** Whether to show tooltip with additional timezone info */
  showTooltip?: boolean
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Custom className */
  className?: string
  /** Whether to show an icon */
  showIcon?: boolean
  /** Custom icon to use */
  icon?: React.ReactNode
  /** Whether to show date only (no time) */
  dateOnly?: boolean
  /** Whether to show time only (no date) */
  timeOnly?: boolean
  /** Whether to prioritize relative time over absolute formatting */
  preferRelative?: boolean
  
  // Global team timezone features
  /** Whether to show multi-timezone tooltip for global teams */
  showGlobalTimezones?: boolean
  /** Team ID for loading team timezone preferences */
  teamId?: string
  /** Custom timezones to show in tooltip (overrides team preferences) */
  globalTimezones?: string[]
  /** Whether to show business hours indicators */
  showBusinessHours?: boolean
  /** Type of global timezone display */
  globalTooltipType?: 'full' | 'compact'
  /** Maximum number of timezones to show in tooltip */
  maxGlobalTimezones?: number
}

export function TimeWithTimezone({
  date,
  format,
  showTimezone = true,
  showRelative = false,
  relativeMode = 'smart',
  relativeContext = 'normal',
  relativeOptions = {},
  showTooltip = true,
  size = 'sm',
  className,
  showIcon = false,
  icon,
  dateOnly = false,
  timeOnly = false,
  preferRelative = false,
  
  // Global timezone features
  showGlobalTimezones = false,
  teamId,
  globalTimezones,
  showBusinessHours = true,
  globalTooltipType = 'full',
  maxGlobalTimezones = 6
}: TimeWithTimezoneProps) {
  const { 
    formatDate, 
    formatTime,
    getAbbreviation, 
    getDisplayName,
    timezone,
    isLoading 
  } = useTimezone()

  // Team timezones for global display
  const { teamMembers, isGlobalTeam } = useTeamTimezones({ 
    teamId: showGlobalTimezones ? teamId : undefined 
  })

  if (isLoading) {
    return (
      <div className={cn('text-muted-foreground animate-pulse flex items-center gap-1', className)}>
        {showIcon && (icon || <Clock className="h-3 w-3" />)}
        <span>Loading...</span>
      </div>
    )
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date
  const abbreviation = getAbbreviation(dateObj)
  const displayName = getDisplayName()

  // Determine format based on props
  const getFormattedDate = () => {
    if (timeOnly) {
      return formatTime(dateObj)
    } else if (dateOnly) {
      return formatDate(dateObj, 'MMM dd, yyyy')
    } else if (format) {
      return formatDate(dateObj, format)
    } else {
      return formatDate(dateObj)
    }
  }

  // Get enhanced relative time if enabled
  const getEnhancedRelativeTime = () => {
    if (!showRelative) return null

    switch (relativeMode) {
      case 'simple':
        const result = getRelativeTime(dateObj, timezone, relativeOptions)
        return result.relative
      case 'smart':
        return getSmartRelativeTime(dateObj, timezone, relativeOptions)
      case 'contextual':
        return getContextualTime(dateObj, timezone, relativeContext)
      default:
        return getSmartRelativeTime(dateObj, timezone, relativeOptions)
    }
  }

  const formattedDate = getFormattedDate()
  const relativeTime = getEnhancedRelativeTime()
  
  // Determine display priority
  const shouldShowRelativeFirst = preferRelative && relativeTime
  const primaryDisplay = shouldShowRelativeFirst ? relativeTime : formattedDate
  const secondaryDisplay = shouldShowRelativeFirst ? formattedDate : relativeTime
  
  // Size classes
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  }

  // Determine which icon to show
  const getDisplayIcon = () => {
    if (!showIcon) return null
    if (icon) return icon
    if (showGlobalTimezones) return <Globe className="h-3 w-3" />
    return timeOnly ? <Clock className="h-3 w-3" /> : <Calendar className="h-3 w-3" />
  }

  // Main display content
  const displayContent = (
    <div className={cn(
      'flex items-center gap-1',
      sizeClasses[size],
      className
    )}>
      {getDisplayIcon()}
      <span className="flex items-center gap-1">
        <span className={shouldShowRelativeFirst ? 'font-medium text-primary' : ''}>
          {primaryDisplay}
        </span>
        {secondaryDisplay && (
          <span className="text-muted-foreground text-xs">
            ({secondaryDisplay})
          </span>
        )}
        {showTimezone && (
          <span className="text-muted-foreground">
            {abbreviation}
          </span>
        )}
      </span>
    </div>
  )

  if (!showTooltip) {
    return displayContent
  }

  // If showing global timezones, use the multi-timezone tooltip
  if (showGlobalTimezones) {
    const timezones = globalTimezones || (isGlobalTeam ? teamMembers.map(m => m.timezone) : undefined)
    
    if (globalTooltipType === 'compact') {
      return (
        <CompactMultiTimezoneTooltip
          date={dateObj}
          timezones={timezones}
          teamMembers={teamMembers}
          showBusinessHours={showBusinessHours}
          maxTimezones={maxGlobalTimezones}
          className="cursor-help"
        />
      )
    }

    return (
      <MultiTimezoneTooltip
        date={dateObj}
        timezones={timezones}
        teamMembers={teamMembers}
        showBusinessHours={showBusinessHours}
        maxTimezones={maxGlobalTimezones}
        trigger={
          <div className="cursor-help">
            {displayContent}
          </div>
        }
      />
    )
  }

  // Standard single-timezone tooltip with detailed information
  const getDetailedRelativeInfo = () => {
    if (!showRelative) return null
    
    const result = getRelativeTime(dateObj, timezone, relativeOptions)
    return {
      relative: result.relative,
      isPast: result.isPast,
      unit: result.unit,
      value: result.value,
      diffMs: result.diffMs
    }
  }

  const relativeInfo = getDetailedRelativeInfo()

  const tooltipContent = (
    <div className="space-y-2 text-xs">
      <div className="font-medium">
        {formatDate(dateObj, 'EEEE, MMMM dd, yyyy \'at\' h:mm a')}
      </div>
      <div className="space-y-1 text-muted-foreground">
        <div>Timezone: {displayName}</div>
        <div>Code: {abbreviation}</div>
        <div>UTC: {dateObj.toISOString()}</div>
        {!timeOnly && (
          <div>Local browser: {dateObj.toLocaleString()}</div>
        )}
        
        {relativeInfo && (
          <div className="border-t pt-1 mt-1">
            <div className="text-xs font-medium mb-1">Relative Time Info:</div>
            <div>Display: {relativeInfo.relative}</div>
            <div>Direction: {relativeInfo.isPast ? 'Past' : 'Future'}</div>
            <div>Primary unit: {relativeInfo.unit}</div>
            {relativeInfo.value !== 0 && (
              <div>Value: {Math.abs(relativeInfo.value)} {relativeInfo.unit}</div>
            )}
            <div>Difference: {Math.abs(relativeInfo.diffMs / 1000).toFixed(0)} seconds</div>
            <div>Mode: {relativeMode}</div>
            {relativeMode === 'contextual' && (
              <div>Context: {relativeContext}</div>
            )}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            {displayContent}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default TimeWithTimezone